import { useCallback, useEffect, useMemo } from 'react'
import { Grid } from 'semantic-ui-react'
import { faCheck, faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import styles from './styles.module.scss'

import {
  Alert,
  Card,
  Icon,
  GridRowColumn,
  Text,
} from '@/componentsV1/BaseComponents'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import { useViewMissingStatementsList } from '@/components/ViewMissingStatementsAccordion'
import { isoToUTCDateTime, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { MissingStatementCard } from '@/features/bookkeeping/end-of-year-review/missing-bank-statements/missing-statement-card'
import {
  UserDocument,
  MissingStatement,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'

interface IMissingStatementsProps {
  missingStatements?: MissingStatement[]
  setIsStepComplete: (isComplete: boolean, isMissingBank?: boolean) => void
}

interface IStatementMonth {
  [month: string]: UserDocument[]
}

interface ImonthYear {
  monthYear: string
  monthYearLabel: string
}

export const MissingStatements = ({
  missingStatements = [],
  setIsStepComplete,
}: IMissingStatementsProps) => {
  const formattedStatements = useViewMissingStatementsList({
    neededStatements: missingStatements,
  })

  const onUploadOrDelete = useCallback(
    (_: number, __: boolean) => {
      setIsStepComplete(true, true)
    },
    [setIsStepComplete]
  )

  const generateMonthStatements = (
    missingStatements: MissingStatement[] | undefined
  ) => {
    if (missingStatements) {
      const statementMonth: ImonthYear[] = []
      const statementMonthLabel: string[] = []
      const monthStatements: IStatementMonth = {}
      for (const accountStatements of missingStatements) {
        for (const statement of accountStatements.statements) {
          const monthYear = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.MONTH_YEAR_LONG
          )
          const yearMonth = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.YEAR_MONTH
          )
          if (!statementMonthLabel.includes(monthYear)) {
            statementMonthLabel.push(monthYear)
            statementMonth.push({
              monthYear: yearMonth,
              monthYearLabel: monthYear,
            })
            monthStatements[yearMonth] = []
          }
        }
      }
    }
  }

  const isMissingStatements = useMemo(() => {
    return missingStatements.length > 0
  }, [missingStatements])

  // Set initial completion state based on whether there are missing statements
  useEffect(() => {
    if (isMissingStatements) {
      setIsStepComplete(false)
    } else {
      setIsStepComplete(true)
    }
  }, [isMissingStatements, setIsStepComplete])

  useEffect(() => {
    generateMonthStatements(missingStatements)
  }, [missingStatements])

  return (
    <Grid>
      <GridRowColumn centerContent className="header-container">
        <StepHeaders
          imageSrc="https://heard-images.s3.amazonaws.com/assets/upload-statement.png"
          imageAlt="uploadStatement"
          imageWidth={180}
          imageHeight={180}
          title="Upload missing bank statements"
          description=""
          customColor="green"
        >
          <Grid>
            <GridRowColumn className="align-left">
              <Text>
                We need these to reconcile your books. Please upload all the
                statements listed below.
              </Text>
            </GridRowColumn>
            <GridRowColumn className="align-left">
              {formattedStatements}
            </GridRowColumn>
            <Grid.Row />
            <GridRowColumn className={styles.container}>
              <MissingStatementCard onUploadOrDelete={onUploadOrDelete} />
            </GridRowColumn>
            {!isMissingStatements && (
              <Card backgroundColor="lightGreen" fullWidth>
                <div className="all-reconnected">
                  <Icon icon={faCheck} color="accentGreen" size="2x" />

                  <div className="reconnected-text">
                    <Text as="h3">All statements uploaded</Text>
                    <Text>Thanks! You’ve uploaded all missing statements.</Text>
                  </div>
                </div>
              </Card>
            )}
          </Grid>
        </StepHeaders>
      </GridRowColumn>
      <GridRowColumn>
        <div className="alert-footer">
          <Alert
            title="Why is this important?"
            style={{ width: '520px' }}
            customIcon={<Icon icon={faInfoCircle} />}
            contentStyle={{ textAlign: 'left' }}
          >
            Reconciliation is a standard practice to ensure all transactions are
            accounted for. We use your bank statements to check that your
            balances match what we have in your books.
          </Alert>
        </div>
      </GridRowColumn>
    </Grid>
  )
}
